import { useState } from "react";
import { FaEnvelope, FaUndo, FaCheck, FaSpinner } from "react-icons/fa";
import { useNotifications } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import { formatShortDate } from "../UI/DesignStandards";

const DismissedInvitations = () => {
  const { dismissedInvites, restoreInvite } = useNotifications();
  const navigate = useNavigate();
  const [restoringInvites, setRestoringInvites] = useState(new Set());

  if (!dismissedInvites || dismissedInvites.length === 0) {
    return null;
  }

  const handleAcceptInvite = (invite) => {
    navigate(`/invite/${invite.id}`);
  };

  const handleRestoreInvite = async (inviteId) => {
    setRestoringInvites((prev) => new Set(prev).add(inviteId));
    await restoreInvite(inviteId);
    setRestoringInvites((prev) => {
      const newSet = new Set(prev);
      newSet.delete(inviteId);
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatShortDate(dateString);
  };

  return (
    <div className="card-board overflow-hidden">
      <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
        <h3 className="font-display font-bold text-lg text-brand-cream tracking-tight flex items-center">
          <FaEnvelope className="mr-2 text-brand-mist" />
          Dismissed Invitations ({dismissedInvites.length})
        </h3>
        <p className="mt-1 text-sm text-brand-mist">
          Restore these if you want to accept them later.
        </p>
      </div>
      <ul className="divide-y divide-brand-charcoal/10">
        {dismissedInvites.map((invite, index) => (
          <li
            key={invite.id}
            className={`px-4 py-4 sm:px-6 ${
              index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center min-w-0">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-lg bg-brand-cream flex items-center justify-center">
                    <FaEnvelope className="h-5 w-5 text-brand-muted" />
                  </div>
                </div>
                <div className="ml-4 min-w-0">
                  <div className="text-sm font-medium text-brand-charcoal">
                    {invite.organization.name}
                    {invite.organization.nameAbbreviation && (
                      <span className="text-brand-muted ml-1">
                        ({invite.organization.nameAbbreviation})
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-brand-muted">
                    Dismissed on {formatDate(invite.seenAt)} • Expires{" "}
                    {formatDate(invite.expiredAt)}
                  </div>
                  <div className="text-xs text-brand-mist mt-1">
                    Invited by: {invite.invitedByUser?.email || "Unknown User"}
                  </div>
                  {invite.organization.description && (
                    <div className="text-xs text-brand-mist mt-1">
                      {invite.organization.description}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleRestoreInvite(invite.id)}
                  disabled={restoringInvites.has(invite.id)}
                  className="text-sm text-brand-charcoal hover:underline flex items-center disabled:opacity-50"
                >
                  {restoringInvites.has(invite.id) ? (
                    <>
                      <FaSpinner className="h-3 w-3 mr-1 animate-spin" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <FaUndo className="h-3 w-3 mr-1" />
                      Restore
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleAcceptInvite(invite)}
                  className="text-sm font-display font-bold text-brand-charcoal hover:underline flex items-center"
                >
                  <FaCheck className="h-3 w-3 mr-1" />
                  Accept
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DismissedInvitations;
